class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = this.drawer?.querySelector('.cart-drawer__overlay');
    this.closeBtn = this.drawer?.querySelector('.cart-drawer__close');
    this.cartBtn = document.querySelector('.cart-btn');
    this.form = document.getElementById('cart-drawer-form');
    this.isUpdating = false;
    this.updateQueue = [];

    if (!this.drawer) return;

    this.init();
  }

  init() {
    // Open cart drawer when cart button is clicked
    this.cartBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    });

    // Close cart drawer
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // Handle continue shopping button clicks (using event delegation)
    this.drawer.addEventListener('click', (e) => {
      if (e.target.hasAttribute('data-cart-drawer-close') || e.target.closest('[data-cart-drawer-close]')) {
        this.close();
      }
    });

    // Handle ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });

    // Handle quantity changes
    this.drawer.addEventListener('click', (e) => {
      const target = e.target.closest('[data-quantity-increase], [data-quantity-decrease], [data-cart-item-remove]');

      if (target?.hasAttribute('data-quantity-increase')) {
        e.preventDefault();
        this.updateQuantity(target, 1);
      } else if (target?.hasAttribute('data-quantity-decrease')) {
        e.preventDefault();
        this.updateQuantity(target, -1);
      } else if (target?.hasAttribute('data-cart-item-remove')) {
        e.preventDefault();
        this.removeItem(target);
      }
    });

    // Handle manual quantity input
    this.drawer.addEventListener('change', (e) => {
      if (e.target.hasAttribute('data-quantity-input')) {
        this.updateQuantityDirect(e.target);
      }
    });

    // Intercept add-to-cart form submissions
    this.handleAddToCart();

    // Listen for cart updates from add-to-cart forms
    document.addEventListener('cart:update', () => {
      this.refresh();
    });

    // Listen for cart:refresh event (used by third-party apps like Appointo)
    document.addEventListener('cart:refresh', () => {
      this.refresh();
    });
    document.documentElement.addEventListener('cart:refresh', () => {
      this.refresh();
    });

    // Intercept fetch requests to /cart/add.js from third-party apps (like Appointo)
    this.interceptCartAddRequests();
  }

  interceptCartAddRequests() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = function(...args) {
      const [url, options] = args;
      const urlString = typeof url === 'string' ? url : url?.url || '';

      return originalFetch.apply(this, args).then(response => {
        // Check if this is a cart/add request
        if (urlString.includes('/cart/add') && response.ok) {
          // Clone response so we can read it and still return original
          response.clone().json().then(() => {
            // Refresh cart drawer and open it after third-party add-to-cart
            setTimeout(() => {
              self.refresh().then(() => {
                self.open();
              });
            }, 100);
          }).catch(() => {});
        }
        return response;
      });
    };
  }

  handleAddToCart() {
    // Find all product forms with ajax cart enabled
    const productForms = document.querySelectorAll('form[data-ajax-cart="true"]');

    productForms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const formData = new FormData(form);
        const submitButton = form.querySelector('button[type="submit"]');

        // Disable submit button during request
        if (submitButton) {
          submitButton.disabled = true;
        }

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            await this.refresh();
            this.open();
          } else {
            const error = await response.json();
            console.error('Error adding to cart:', error);
            alert(error.description || 'Error adding product to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          alert('Error adding product to cart');
        } finally {
          // Re-enable submit button
          if (submitButton) {
            submitButton.disabled = false;
          }
        }
      });
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async updateQuantity(button, change) {
    if (this.isUpdating) return;

    const key = button.getAttribute('data-key');
    const input = this.drawer.querySelector(`input[data-key="${key}"]`);
    const currentQty = parseInt(input.value) || 0;
    const newQty = Math.max(0, currentQty + change);

    // Update UI optimistically
    input.value = newQty;
    this.setLoadingState(key, true);

    await this.updateCart(key, newQty);
  }

  async updateQuantityDirect(input) {
    if (this.isUpdating) return;

    const key = input.getAttribute('data-key');
    let newQty = parseInt(input.value) || 0;

    if (newQty < 0) {
      newQty = 0;
      input.value = 0;
    }

    this.setLoadingState(key, true);
    await this.updateCart(key, newQty);
  }

  async removeItem(button) {
    if (this.isUpdating) return;

    const key = button.getAttribute('data-key');
    const cartItem = button.closest('.cart-item');

    // Add visual feedback
    if (cartItem) {
      cartItem.style.opacity = '0.5';
      cartItem.style.pointerEvents = 'none';
    }

    await this.updateCart(key, 0);
  }

  setLoadingState(key, isLoading) {
    const buttons = this.drawer.querySelectorAll(`[data-key="${key}"]`);
    buttons.forEach(button => {
      if (button.tagName === 'BUTTON') {
        button.disabled = isLoading;
      } else if (button.tagName === 'INPUT') {
        button.disabled = isLoading;
      }
    });
  }

  async updateCart(key, quantity) {
    // Add to queue if already updating
    if (this.isUpdating) {
      this.updateQueue.push({ key, quantity });
      return;
    }

    this.isUpdating = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (response.ok) {
        const cart = await response.json();
        await this.refreshFromCartData(cart);
      } else {
        console.error('Error updating cart');
        // Refresh to restore correct state
        await this.refresh();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      // Refresh to restore correct state
      await this.refresh();
    } finally {
      this.setLoadingState(key, false);
      this.isUpdating = false;

      // Process queue
      if (this.updateQueue.length > 0) {
        const nextUpdate = this.updateQueue.shift();
        await this.updateCart(nextUpdate.key, nextUpdate.quantity);
      }
    }
  }

  async refreshFromCartData(cart) {
    try {
      // Update cart drawer by fetching fresh HTML
      const response = await fetch(window.location.pathname, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update cart drawer content
      const newDrawerBody = doc.querySelector('.cart-drawer__body');
      const newDrawerFooter = doc.querySelector('.cart-drawer__footer');
      const currentDrawerFooter = this.drawer.querySelector('.cart-drawer__footer');

      if (newDrawerBody) {
        const currentDrawerBody = this.drawer.querySelector('.cart-drawer__body');
        currentDrawerBody.innerHTML = newDrawerBody.innerHTML;
      }

      // Handle footer - either update it or remove it
      if (newDrawerFooter) {
        // Cart has items - footer exists in new HTML
        if (currentDrawerFooter) {
          // Footer already exists, update it
          currentDrawerFooter.innerHTML = newDrawerFooter.innerHTML;
        } else {
          // Footer doesn't exist yet, insert it after body
          const currentDrawerBody = this.drawer.querySelector('.cart-drawer__body');
          currentDrawerBody.insertAdjacentElement('afterend', newDrawerFooter);
        }
      } else {
        // Cart is empty - footer shouldn't exist
        if (currentDrawerFooter) {
          currentDrawerFooter.remove();
        }
      }

      // Update cart count
      this.updateCartCount(cart.item_count);

      // Dispatch event
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (error) {
      console.error('Error refreshing cart from data:', error);
    }
  }

  async refresh() {
    try {
      const response = await fetch(window.location.pathname, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update cart drawer content
      const newDrawerBody = doc.querySelector('.cart-drawer__body');
      const newDrawerFooter = doc.querySelector('.cart-drawer__footer');
      const currentDrawerFooter = this.drawer.querySelector('.cart-drawer__footer');

      if (newDrawerBody) {
        const currentDrawerBody = this.drawer.querySelector('.cart-drawer__body');
        currentDrawerBody.innerHTML = newDrawerBody.innerHTML;
      }

      // Handle footer - either update it or remove it
      if (newDrawerFooter) {
        // Cart has items - footer exists in new HTML
        if (currentDrawerFooter) {
          // Footer already exists, update it
          currentDrawerFooter.innerHTML = newDrawerFooter.innerHTML;
        } else {
          // Footer doesn't exist yet, insert it after body
          const currentDrawerBody = this.drawer.querySelector('.cart-drawer__body');
          currentDrawerBody.insertAdjacentElement('afterend', newDrawerFooter);
        }
      } else {
        // Cart is empty - footer shouldn't exist
        if (currentDrawerFooter) {
          currentDrawerFooter.remove();
        }
      }

      // Update cart count in header
      const cartData = await fetch('/cart.js');
      const cart = await cartData.json();
      this.updateCartCount(cart.item_count);

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  updateCartCount(count) {
    const cartCountElement = document.querySelector('.cart-btn_count');
    if (cartCountElement) {
      cartCountElement.textContent = count;
      cartCountElement.style.display = count > 0 ? 'flex' : 'none';
    }
    const drawerCountElement = document.querySelector('.cart-drawer__count');
    if (drawerCountElement) {
      drawerCountElement.textContent = count;
      drawerCountElement.style.display = count > 0 ? 'flex' : 'none';
    }
  }
}

// Initialize cart drawer when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartDrawer());
} else {
  new CartDrawer();
}
